import {
  BaseQueryFn,
  EndpointBuilder,
  QueryDefinition,
  MutationDefinition,
  InfiniteQueryDefinition,
} from '@reduxjs/toolkit/query';

import type { IUserNotification } from 'interfaces/UserNotification.interface';
import type {
  ICollection,
  ICollectionApiResponse,
  IInfinitePaginationArgs,
} from 'services/api/api.types';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import type {
  IGetUserNotificationItemArgs,
  IUserNotificationCollectionArgs,
  IUserNotificationPutArgs,
} from 'services/userNotifications/userNotifications.types';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getUserNotifications = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IUserNotificationCollectionArgs,
  BaseQueryFn,
  string,
  ICollection<IUserNotification>
> =>
  build.query<ICollection<IUserNotification>, IUserNotificationCollectionArgs>({
    providesTags: ['UserNotification'],
    query: (queryArg: IUserNotificationCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);

      return {
        url: `api/user_notifications${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IUserNotification>
    ): ICollection<IUserNotification> =>
      transformCollectionResponse<IUserNotification>(response),
  });

export const getInfiniteUserNotifications = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): InfiniteQueryDefinition<
  IUserNotificationCollectionArgs,
  IInfinitePaginationArgs,
  BaseQueryFn,
  string,
  ICollection<IUserNotification>
> =>
  build.infiniteQuery<
    ICollection<IUserNotification>,
    IUserNotificationCollectionArgs,
    IInfinitePaginationArgs
  >({
    providesTags: ['UserNotification'],
    infiniteQueryOptions: {
      // Must provide a default initial page param value
      initialPageParam: {
        page: 1,
        itemsPerPage: 10,
      },
      getNextPageParam: (
        lastPage,
        _allPages,
        { itemsPerPage, page: pageLastPage },
        _allPageParams
      ) => {
        const totalCount = lastPage?.totalCount;
        if (!totalCount || !itemsPerPage) {
          return undefined;
        }
        if (totalCount <= itemsPerPage * pageLastPage || totalCount === 0) {
          return undefined;
        }
        return { page: pageLastPage + 1, itemsPerPage };
      },
      getPreviousPageParam: (
        _firstPage,
        _allPages,
        { page: pageFirstPage, itemsPerPage },
        _allPageParams
      ) => {
        const prevPage = pageFirstPage - 1;
        if (prevPage < 1) {
          return undefined;
        }
        return { page: prevPage, itemsPerPage };
      },
    },
    query: ({ queryArg, pageParam: { page, itemsPerPage } }) => {
      const paginationParamObject = {
        page,
        itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return `api/user_notifications${paginationQuery}${filterQuery}`;
    },
    transformResponse: (
      response: ICollectionApiResponse<IUserNotification>
    ): ICollection<IUserNotification> =>
      transformCollectionResponse<IUserNotification>(response),
  });

export const getUserNotificationItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetUserNotificationItemArgs,
  BaseQueryFn,
  string,
  IUserNotification
> =>
  build.query<IUserNotification, IGetUserNotificationItemArgs>({
    providesTags: ['UserNotification'],
    query: (queryArg: IGetUserNotificationItemArgs) => ({
      url: `api/user_notifications/${queryArg.id}`,
    }),
  });

export const putUserNotificationItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IUserNotificationPutArgs,
  BaseQueryFn,
  string,
  IUserNotification
> =>
  build.mutation<IUserNotification, IUserNotificationPutArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['UserNotification'],
    query: (queryArg) => ({
      url: `api/user_notifications/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const postUserNotificationsMarkAllAsRead = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<void, BaseQueryFn, string, IUserNotification> =>
  build.mutation<IUserNotification, void>({
    invalidatesTags: ['UserNotification'],
    query: () => ({
      url: 'api/user_notifications/set_all_read',
      method: 'POST',
    }),
  });

export const postUserNotificationsUnsetAllNew = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<null, BaseQueryFn, string, IUserNotification> =>
  build.mutation<IUserNotification, null>({
    invalidatesTags: ['UserNotification'],
    query: () => ({
      url: 'api/user_notifications/unset_all_new',
      method: 'POST',
    }),
  });
